import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { FeedFilterTypeEnum, FeedItemEntity, FeedItemTypeEnum, OrderController } from '@domain';
import { RaButtonComponent, RaInputComponent, RaLoadingComponent } from '@shared/components';
import { RaBadgeComponent } from 'ra-ui';
import { debounceTime, distinctUntilChanged, take } from 'rxjs';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { FeedMainService } from '../../services/feed-main.service';
import { OrderMainService } from '../../services/order-main.service';

@Component({
    selector: 'order-archived-panel',
    templateUrl: 'order-archived-panel.component.html',
    styleUrl: 'order-archived-panel.component.scss',
    imports: [
        RaModalComponent,
        RaBadgeComponent,
        RaEmptyComponent,
        RaInputComponent,
        RaLoadingComponent,
        RaButtonComponent,
        RaSquareIconComponent,
        ReactiveFormsModule,
        TranslatePipe,
    ],
})
export class OrderArchivedPanelComponent implements OnInit {
    public close = output<void>();
    public searchControl = new FormControl('');
    public restoringOrderId = signal<string | null>(null);

    /**
     * `true` = painel aberto pelo FEED unificado: lista arquivados MISTOS
     * (grupos + documentos avulsos) via `feed?archived=true`, com restauração
     * pelo reopen da order (vale para a order pessoal do avulso).
     * `false` (default) = comportamento clássico (só serviços), intacto.
     */
    public feedMode = input<boolean>(false);

    public feedItems = signal<FeedItemEntity[]>([]);
    public feedLoading = signal<boolean>(false);
    public feedItemTypeEnum = FeedItemTypeEnum;

    /** Sequência das buscas do modo feed. */
    private _feedSeq = 0;

    // * [Injects]
    public orderMainService = inject(OrderMainService);
    private _orderController = inject(OrderController);
    private _feedMainService = inject(FeedMainService);

    ngOnInit(): void {
        this._listenSearchControl();
        this.getOrders();
    }

    /** Título do item arquivado no modo feed: grupo = cliente (fallback descrição). */
    public feedItemTitle(item: FeedItemEntity): string {
        if (item.type === FeedItemTypeEnum.GROUP) {
            return item.client?.name || item.description?.trim() || '-';
        }

        return item.document?.title || '-';
    }

    private _getArchivedFeed(): void {
        // Busca nova invalida a anterior: a resposta que chega por último não pode ganhar
        const seq = ++this._feedSeq;

        this.feedLoading.set(true);
        this._orderController
            .feed({ page: 1, limit: 50 }, this.searchControl.value, FeedFilterTypeEnum.ALL, true)
            .pipe(take(1))
            .subscribe({
                next: paginate => {
                    if (seq !== this._feedSeq) return;
                    this.feedItems.set(paginate?.data ?? []);
                    this.feedLoading.set(false);
                },
                error: () => {
                    if (seq === this._feedSeq) this.feedLoading.set(false);
                },
            });
    }

    /** Restaura um item arquivado do modo feed (reopen da order/order pessoal). */
    public restoreFeedItem(item: FeedItemEntity): void {
        this.restoringOrderId.set(item._id);

        this._orderController
            .restore(item._id)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.restoringOrderId.set(null);
                    this.feedItems.update(items => items.filter(i => i._id !== item._id));
                    this._feedMainService.refreshSilent();
                },
                error: () => this.restoringOrderId.set(null),
            });
    }

    /**
     * Escuta as mudanças no input de busca
     */
    private _listenSearchControl(): void {
        this.searchControl.valueChanges
            // Sem `skip(1)`: `valueChanges` não emite o valor inicial, e pular o primeiro
            // evento fazia uma busca colada de uma vez só não disparar
            .pipe(debounceTime(600), distinctUntilChanged())
            .subscribe(() => this.getOrders());
    }

    /**
     * Busca os arquivados (feed misto no modo feed; só serviços no clássico)
     */
    public getOrders(page: number = 1): void {
        if (this.feedMode()) {
            this._getArchivedFeed();

            return;
        }

        // O campo de busca nunca é travado durante a busca: desabilitar tirava o foco
        // e engolia o que era digitado. O loading aparece no próprio campo.
        this.orderMainService.getArchivedOrders(this.searchControl.value, page).subscribe();
    }

    /**
     * Restaura um serviço arquivado
     * @param id ID do serviço
     */
    public restoreOrder(id: string): void {
        this.restoringOrderId.set(id);

        this.orderMainService.restoreOrder(id).subscribe({
            next: () => this.restoringOrderId.set(null),
            error: () => this.restoringOrderId.set(null),
        });
    }
}
